import { difference, includes, omit, sortedUniq, uniqBy } from "lodash"
import { call, put, select, takeEvery } from "redux-saga/effects"

import * as gql from "../../../common/graphql"
import { nearest } from "../../common/locationSupport"

import {
  HOME_FETCH_LG_DETAIL_BEGIN,
  HOME_FETCH_LG_DETAIL_DISMISS_ERROR,
  HOME_FETCH_LG_DETAIL_FAILURE,
  HOME_FETCH_LG_DETAIL_SUCCESS,
} from "./constants"

export const LG_DETAIL_CACHING = {
  maxFetch: 30,
  maxRetain: 90,
}

export const fetchLgDetail = (authToken, references) => ({
  type: HOME_FETCH_LG_DETAIL_BEGIN,
  data: { authToken, references },
})

export const dismissFetchLgDetailError = references => ({
  type: HOME_FETCH_LG_DETAIL_DISMISS_ERROR,
})

// worker Saga: will be fired on HOME_FETCH_LG_DETAIL_BEGIN actions
export function* doFetchLgDetail({ data: { authToken, references } }) {
  if (!authToken) {
    authToken = yield select(state => state.auth.authToken)
  }

  const { data, graphqlErrors, networkError } = yield call(gqlFetchLgDetail, authToken, references)

  if (data) {
    const dataByRef = {}
    const errorsByRef = {}
    Object.getOwnPropertyNames(data).forEach(lgRef => {
      const ref = lgRef.substring(3) // Strip the "LG_" prefix away

      const detail = data[lgRef]
      if (detail) dataByRef[ref] = detail

      const errors = (graphqlErrors || []).filter(error => (error.path || [])[0] === lgRef)
      if (errors.length > 0) errorsByRef[ref] = errors
    })

    const refsWithData = Object.getOwnPropertyNames(dataByRef)
    if (refsWithData.length > 0) {
      yield put({
        type: HOME_FETCH_LG_DETAIL_SUCCESS,
        data: dataByRef,
      })
    }

    const refsWithErrors = Object.getOwnPropertyNames(errorsByRef)
    if (refsWithErrors.length > 0) {
      yield put({
        type: HOME_FETCH_LG_DETAIL_FAILURE,
        data: {
          error: { graphqlErrors: refsWithErrors.flatMap(ref => errorsByRef[ref]) },
          references: refsWithErrors,
        },
      })
    }
  } else {
    yield put({
      type: HOME_FETCH_LG_DETAIL_FAILURE,
      data: { error: { graphqlErrors, networkError }, references },
    })
  }
}

export function gqlFetchLgDetail(authToken, references) {
  return gql.post({
    authToken,
    queryName: "fetchLgDetail",
    query: `query fetchLgDetail { ${references.map(detailFragment)} }`,
  })
}

function detailFragment(ref) {
  return `
      LG_${ref}: property(reference: "${ref}")  {
        houses {
          reference
          description
          address {
            street
            zipCode
            city
          }

          units {
            reference
            unitTypeCode
            numberOfRooms
            tenantName
          }
        }
      }
    `
}

/*
  Alternatively you may use takeEvery.

  takeLatest does not allow concurrent requests. If an action gets
  dispatched while another is already pending, that pending one is cancelled
  and only the latest one will be run.
*/
export function* watchFetchLgDetail() {
  yield takeEvery(HOME_FETCH_LG_DETAIL_BEGIN, doFetchLgDetail)
}

// Redux reducer
export function reducer(state, action) {
  switch (action.type) {
    case HOME_FETCH_LG_DETAIL_BEGIN:
      return {
        ...state,
        fetchLgDetailPending: sortedUniq(state.fetchLgDetailPending.concat(action.data.references).sort()),
        fetchLgDetailError: omit(state.fetchLgDetailError, action.data.references),
      }

    case HOME_FETCH_LG_DETAIL_SUCCESS:
      const oldProperties = (state.lhoData && state.lhoData.properties) || []

      const knownRefs = oldProperties.map(p => p.reference)
      const amendingRefs = Object.getOwnPropertyNames(action.data)
      const missing = difference(amendingRefs, knownRefs)
      if (missing.length) console.log("Warning: Could not amend all targeted properties, please refresh.", { missing })

      // NB: Keep the same order of properties (sorted by reference)
      const amendedProperties = []
      oldProperties.forEach(oldProperty => {
        if (action.data.hasOwnProperty(oldProperty.reference)) {
          const amendments = action.data[oldProperty.reference]
          amendedProperties.push({ ...oldProperty, ...amendments })
        } else {
          amendedProperties.push(oldProperty)
        }
      })

      // Retain detail data for the closest properties (and always keep the data we just fetched)
      const nearestPropertiesHavingHouses = nearest(
        LG_DETAIL_CACHING.maxRetain,
        amendedProperties.filter(p => p.hasOwnProperty("houses")),
      )
      const nearestRefs = nearestPropertiesHavingHouses.map(prop => prop.reference)
      const keepRefs = nearestRefs.concat(amendingRefs)

      const newProperties = amendedProperties.map(prop => {
        if (includes(keepRefs, prop.reference)) {
          return prop
        } else {
          return omit(prop, "houses")
        }
      })

      return {
        ...state,
        lhoData: { ...state.lhoData, properties: newProperties },
        fetchLgDetailPending: difference(state.fetchLgDetailPending, amendingRefs),
        fetchLgDetailError: omit(state.fetchLgDetailError, amendingRefs),
      }

    case HOME_FETCH_LG_DETAIL_FAILURE:
      const {
        error: { graphqlErrors, networkError },
        references,
      } = action.data

      const errorsByPropertyRef = {}
      references.forEach(ref => {
        errorsByPropertyRef[ref] = { graphqlErrors: uniqBy(graphqlErrors, JSON.stringify), networkError }
      })

      return {
        ...state,
        fetchLgDetailPending: difference(state.fetchLgDetailPending, action.data.references),
        fetchLgDetailError: { ...state.fetchLgDetailError, ...errorsByPropertyRef },
      }

    case HOME_FETCH_LG_DETAIL_DISMISS_ERROR:
      return {
        ...state,
        fetchLgDetailError: omit(state.fetchLgDetailError, action.data.references),
      }

    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/features/home/redux/fetchLgDetail.js